import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { setAdsenseConsentSafety } from '../../services/ads/adsenseSafetyGate.js';
import '../../styles/consent.css';

const STORAGE_KEY = 'watchvim_privacy_preferences_v1';
const VERSION = 1;

const defaultPreferences = Object.freeze({
  version: VERSION,
  decided: false,
  essential: true,
  analytics: false,
  advertising: false,
  personalization: false,
  updatedAt: null,
});

const ConsentContext = createContext(null);

function ensureAdsenseQueuePaused() {
  if (typeof window === 'undefined') return;
  window.adsbygoogle = window.adsbygoogle || [];
  window.adsbygoogle.pauseAdRequests = 1;
  window.adsbygoogle.requestNonPersonalizedAds = 1;
}

// Establish the conservative state before any child component can mount an AdSense unit.
ensureAdsenseQueuePaused();

function normalizePreferences(value = {}) {
  return {
    version: VERSION,
    decided: value.decided === true,
    essential: true,
    analytics: value.analytics === true,
    advertising: value.advertising === true,
    personalization: value.advertising === true && value.personalization === true,
    updatedAt: value.updatedAt || null,
  };
}

function readStoredPreferences() {
  if (typeof window === 'undefined') return { ...defaultPreferences };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    return parsed?.version === VERSION ? normalizePreferences(parsed) : { ...defaultPreferences };
  } catch (_) {
    return { ...defaultPreferences };
  }
}

function persistPreferences(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (_) {}
}

function consentState(value) {
  return value ? 'granted' : 'denied';
}

function applyGoogleConsentMode(preferences, command = 'update') {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag('consent', command, {
    ad_storage: consentState(preferences.advertising),
    ad_user_data: consentState(preferences.personalization),
    ad_personalization: consentState(preferences.personalization),
    analytics_storage: consentState(preferences.analytics),
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
}

function applyAdsensePrivacyState(preferences) {
  setAdsenseConsentSafety(preferences);
}

function googlePrivacyMessageAvailable() {
  return typeof window !== 'undefined'
    && Boolean(window.googlefc?.callbackQueue)
    && typeof window.googlefc?.showRevocationMessage === 'function';
}

function PreferenceRow({ title, description, checked, disabled, onChange }) {
  return (
    <label className="consent-preference-row">
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange?.(event.target.checked)} />
    </label>
  );
}

export function ConsentProvider({ children }) {
  const [preferences, setPreferences] = useState(readStoredPreferences);
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState(() => readStoredPreferences());
  const [googleMessageStatus, setGoogleMessageStatus] = useState('');

  useEffect(() => {
    applyGoogleConsentMode(defaultPreferences, 'default');
    applyAdsensePrivacyState(preferences);
  }, []);

  useEffect(() => {
    applyAdsensePrivacyState(preferences);
    if (!preferences.decided) return;
    applyGoogleConsentMode(preferences, 'update');
  }, [preferences]);

  function commit(next) {
    const normalized = normalizePreferences({ ...next, decided: true, updatedAt: new Date().toISOString() });
    persistPreferences(normalized);
    setPreferences(normalized);
    setDraft(normalized);
    setPanelOpen(false);
    return normalized;
  }

  function acceptAll() {
    commit({ analytics: true, advertising: true, personalization: true });
  }

  function rejectNonEssential() {
    commit({ analytics: false, advertising: false, personalization: false });
  }

  function openPreferences() {
    setDraft(preferences);
    setPanelOpen(true);
    setGoogleMessageStatus('');
  }

  function closePreferences() {
    setPanelOpen(false);
    setGoogleMessageStatus('');
  }

  function savePreferences() {
    commit(draft);
  }

  function openGooglePrivacyChoices() {
    if (googlePrivacyMessageAvailable()) {
      window.googlefc.callbackQueue.push(window.googlefc.showRevocationMessage);
      setGoogleMessageStatus('Opening Google advertising privacy choices…');
      return;
    }
    setGoogleMessageStatus('Google advertising privacy choices become available when Google Privacy & Messaging is active on this visit. Your WatchVIM cookie choices remain saved here.');
  }

  const value = useMemo(() => ({
    preferences,
    acceptAll,
    rejectNonEssential,
    openPreferences,
    hasAdvertisingConsent: preferences.decided && preferences.advertising,
    requestNonPersonalizedAds: preferences.decided && preferences.advertising && !preferences.personalization,
  }), [preferences]);

  const showBanner = !preferences.decided && !panelOpen;

  return (
    <ConsentContext.Provider value={value}>
      {children}

      {showBanner ? (
        <section className="consent-banner" role="dialog" aria-modal="false" aria-labelledby="consent-banner-title">
          <div className="consent-banner-copy">
            <strong id="consent-banner-title">Your privacy choices</strong>
            <p>
              WatchVIM uses essential technologies to operate the service. With your permission, we may also use analytics and advertising technologies. Google may present an additional certified regional consent message where required.
            </p>
          </div>
          <div className="consent-banner-actions">
            <button type="button" className="consent-secondary" onClick={rejectNonEssential}>Reject Non-Essential</button>
            <button type="button" className="consent-secondary" onClick={openPreferences}>Manage Preferences</button>
            <button type="button" className="consent-primary" onClick={acceptAll}>Accept All</button>
          </div>
        </section>
      ) : null}

      {panelOpen ? (
        <div className="consent-modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closePreferences();
        }}>
          <section className="consent-modal" role="dialog" aria-modal="true" aria-labelledby="consent-modal-title">
            <header>
              <div>
                <span className="eyebrow">WatchVIM Privacy</span>
                <h2 id="consent-modal-title">Cookie Preferences</h2>
              </div>
              <button type="button" className="consent-close" onClick={closePreferences} aria-label="Close cookie preferences">×</button>
            </header>

            <p className="consent-intro">Choose which non-essential technologies WatchVIM may use. Free-with-ads titles still include contextual WatchVIM ads when optional advertising technologies are off. Essential storage remains enabled because it supports security, account access, playback, and core site functions.</p>

            <div className="consent-preferences">
              <PreferenceRow title="Essential" description="Security, authentication, account sessions, playback continuity, and core site functions." checked disabled />
              <PreferenceRow title="Analytics" description="Helps WatchVIM understand performance, engagement, and technical issues." checked={draft.analytics} onChange={(checked) => setDraft((current) => ({ ...current, analytics: checked }))} />
              <PreferenceRow title="Advertising" description="Allows third-party advertising technologies, including Google advertising, to be requested where otherwise eligible." checked={draft.advertising} onChange={(checked) => setDraft((current) => ({ ...current, advertising: checked, personalization: checked ? current.personalization : false }))} />
              <PreferenceRow title="Personalized Advertising" description="Allows advertising personalization signals where permitted and where a certified regional CMP also records any required consent." checked={draft.personalization} disabled={!draft.advertising} onChange={(checked) => setDraft((current) => ({ ...current, personalization: checked }))} />
            </div>

            <div className="consent-google-note">
              <strong>Google regional privacy choices</strong>
              <p>For eligible EEA, UK, Switzerland, and other supported regulatory experiences, Google’s certified Privacy & Messaging CMP remains authoritative for Google advertising consent signals.</p>
              <button type="button" className="consent-link-button" onClick={openGooglePrivacyChoices}>Open Google Privacy Choices</button>
              {googleMessageStatus ? <small role="status">{googleMessageStatus}</small> : null}
            </div>

            <div className="consent-modal-actions">
              <button type="button" className="consent-secondary" onClick={rejectNonEssential}>Reject Non-Essential</button>
              <button type="button" className="consent-primary" onClick={savePreferences}>Save Preferences</button>
            </div>
          </section>
        </div>
      ) : null}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const value = useContext(ConsentContext);
  if (!value) throw new Error('useConsent must be used inside ConsentProvider');
  return value;
}
